import { useEffect, useState } from 'react';
import { Button, Divider, Empty, Form, Input, InputNumber, Select, Space, Switch, Tag, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { RuleChainEdge, RuleChainGraph, RuleChainNode } from '../../api/ruleChains';
import { getRuleNodeDefinition, type RuleNodeField } from '@dtwin/shared';

const { Text } = Typography;
const { TextArea } = Input;

function JsonField({ value, onChange, testId }: { value: unknown; onChange: (v: unknown) => void; testId: string }) {
  const pretty = (v: unknown) => {
    try { return JSON.stringify(v ?? null, null, 2); } catch { return String(v ?? ''); }
  };
  const [text, setText] = useState(pretty(value));
  useEffect(() => setText(pretty(value)), [value]);
  return (
    <TextArea
      rows={5}
      value={text}
      spellCheck={false}
      style={{ fontFamily: 'Consolas, monospace', fontSize: 12 }}
      onChange={(e) => {
        setText(e.target.value);
        try { onChange(JSON.parse(e.target.value)); } catch { /* 保留上次合法值 */ }
      }}
      data-testid={testId}
    />
  );
}

function NodeField({ field, value, onChange }: { field: RuleNodeField; value: unknown; onChange: (v: unknown) => void }) {
  const testId = `rule-node-field-${field.key}`;
  if (field.type === 'json') return <JsonField value={value ?? field.default} onChange={onChange} testId={testId} />;
  if (field.type === 'boolean') return <Switch checked={Boolean(value ?? field.default)} onChange={onChange} data-testid={testId} />;
  if (field.type === 'number') {
    return <InputNumber min={field.min} max={field.max} value={Number(value ?? field.default ?? 0)} onChange={(v) => onChange(v)} style={{ width: '100%' }} data-testid={testId} />;
  }
  if (field.type === 'select') {
    return <Select value={String(value ?? field.default ?? '')} onChange={onChange} options={field.options} data-testid={testId} />;
  }
  if (field.type === 'code') {
    return <TextArea rows={7} value={String(value ?? field.default ?? '')} onChange={(e) => onChange(e.target.value)} spellCheck={false} style={{ fontFamily: 'Consolas, monospace', fontSize: 12 }} data-testid={testId} />;
  }
  if (field.type === 'secret') {
    return <Input.Password value={String(value ?? field.default ?? '')} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
  }
  return <Input value={String(value ?? field.default ?? '')} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} data-testid={testId} />;
}

export interface RuleNodeConfigPanelProps {
  graph: RuleChainGraph;
  nodeId?: string | null;
  edgeId?: string | null;
  onChange: (graph: RuleChainGraph) => void;
  onDeleted?: () => void;
}

export default function RuleNodeConfigPanel({ graph, nodeId, edgeId, onChange, onDeleted }: RuleNodeConfigPanelProps) {
  const node = nodeId ? graph.nodes.find((n) => n.id === nodeId) : undefined;
  const edge = edgeId ? graph.edges.find((e) => (e.id || `${e.from}-${e.to}`) === edgeId) : undefined;

  const updateNode = (patch: Partial<RuleChainNode>) => {
    if (!node) return;
    onChange({
      ...graph,
      nodes: graph.nodes.map((n) => (n.id === node.id ? { ...n, ...patch } : n)),
    });
  };
  const updateConfig = (key: string, value: unknown) => {
    if (!node) return;
    updateNode({ config: { ...(node.config || {}), [key]: value } });
  };
  const updateParam = (key: string, value: unknown) => {
    if (!node) return;
    const params = { ...((node.config?.params as Record<string, unknown>) || {}), [key]: value };
    updateConfig('params', params);
  };
  const removeNode = () => {
    if (!node) return;
    onChange({
      nodes: graph.nodes.filter((n) => n.id !== node.id),
      edges: graph.edges.filter((e) => e.from !== node.id && e.to !== node.id),
    });
    onDeleted?.();
  };
  const updateEdge = (patch: Partial<RuleChainEdge>) => {
    if (!edge) return;
    const id = edge.id || `${edge.from}-${edge.to}`;
    onChange({
      ...graph,
      edges: graph.edges.map((e) => ((e.id || `${e.from}-${e.to}`) === id ? { ...e, ...patch, id } : e)),
    });
  };
  const removeEdge = () => {
    if (!edge) return;
    const id = edge.id || `${edge.from}-${edge.to}`;
    onChange({ ...graph, edges: graph.edges.filter((e) => (e.id || `${e.from}-${e.to}`) !== id) });
    onDeleted?.();
  };

  if (edge) {
    return (
      <div data-testid="rule-edge-config">
        <Text strong>连线</Text>
        <Tag style={{ marginInlineStart: 6 }}>{edge.from} → {edge.to}</Tag>
        <Divider style={{ margin: '8px 0' }} />
        <Form layout="vertical" size="small">
          <Form.Item label="条件（可选）" help="如 msg.data.v > 5；为空=始终通过">
            <Input
              value={edge.condition || ''}
              onChange={(e) => updateEdge({ condition: e.target.value || undefined })}
              placeholder="msg.data.v > 5"
              data-testid="rule-edge-condition"
            />
          </Form.Item>
          <Form.Item label="输出分支（可选）" help="Switch/RPC 等节点的分支端口，如 case0 / otherwise / true / false">
            <Input value={edge.fromPort || ''} onChange={(e) => updateEdge({ fromPort: e.target.value || undefined })} placeholder="case0" data-testid="rule-edge-from-port" />
          </Form.Item>
          <Button danger icon={<DeleteOutlined />} onClick={removeEdge} data-testid="rule-edge-delete">删除连线</Button>
        </Form>
      </div>
    );
  }

  if (!node) {
    return <Empty description="点击画布节点或连线进行配置" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const def = node.kind ? getRuleNodeDefinition(node.type, node.kind) : undefined;
  if (def) {
    return (
      <div data-testid="rule-node-config">
        <Space>
          <Text strong>{def.title}</Text>
          <Tag>{def.type}/{def.kind}</Tag>
        </Space>
        <Divider style={{ margin: '8px 0' }} />
        <Form layout="vertical" size="small">
          <Form.Item label="名称">
            <Input value={node.name || ''} onChange={(e) => updateNode({ name: e.target.value })} placeholder={node.id} data-testid="rule-node-name" />
          </Form.Item>
          {def.fields.map((f) => (
            <Form.Item key={f.key} label={f.label} help={f.help} required={f.required}>
              <NodeField field={f} value={node.config?.[f.key]} onChange={(v) => updateConfig(f.key, v)} />
            </Form.Item>
          ))}
          <Divider style={{ margin: '10px 0' }}>容错</Divider>
          <Form.Item label="错误策略">
            <Select
              value={node.errorPolicy || 'stop'}
              onChange={(v) => updateNode({ errorPolicy: v as RuleChainNode['errorPolicy'] })}
              options={[
                { value: 'stop', label: 'stop（停止链）' },
                { value: 'skip', label: 'skip（跳过继续）' },
                { value: 'fallback', label: 'fallback（用输入继续）' },
              ]}
              data-testid="rule-node-error-policy"
            />
          </Form.Item>
          <Form.Item label="重试次数" help="失败或超时后最多尝试次数（1~5）">
            <InputNumber min={1} max={5} precision={0} value={node.retry?.maxAttempts ?? 1} onChange={(v) => updateNode({ retry: { ...(node.retry || {}), maxAttempts: Number(v) || 1 } })} style={{ width: '100%' }} data-testid="rule-node-retry-max" />
          </Form.Item>
          <Form.Item label="重试间隔(ms)">
            <InputNumber min={0} max={10000} step={100} value={node.retry?.backoffMs ?? 0} onChange={(v) => updateNode({ retry: { ...(node.retry || {}), backoffMs: Number(v) || 0 } })} style={{ width: '100%' }} data-testid="rule-node-retry-backoff" />
          </Form.Item>
          <Form.Item label="节点超时(ms)">
            <InputNumber min={200} max={30000} step={100} value={node.timeoutMs ?? 5000} onChange={(v) => updateNode({ timeoutMs: Number(v) || 5000 })} style={{ width: '100%' }} data-testid="rule-node-timeout" />
          </Form.Item>
          <Button danger icon={<DeleteOutlined />} onClick={removeNode} data-testid="rule-node-delete">删除节点</Button>
        </Form>
      </div>
    );
  }

  return (
    <div data-testid="rule-node-config">
      <Space>
        <Text strong>节点配置</Text>
        <Tag>{node.type}</Tag>
      </Space>
      <Divider style={{ margin: '8px 0' }} />
      <Form layout="vertical" size="small">
        <Form.Item label="名称">
          <Input value={node.name || ''} onChange={(e) => updateNode({ name: e.target.value })} placeholder={node.id} data-testid="rule-node-name" />
        </Form.Item>

        {node.type === 'source' && (
          <>
            <Form.Item label="触发类型">
              <Select
                value={String(node.config?.triggerType || 'device')}
                onChange={(v) => updateConfig('triggerType', v)}
                options={[
                  { value: 'device', label: '设备状态（device）' },
                  { value: 'mqtt', label: 'MQTT 主题（mqtt）' },
                  { value: 'schedule', label: '定时（schedule）' },
                  { value: 'manual', label: '仅手动（manual）' },
                ]}
                data-testid="rule-node-trigger-type"
              />
            </Form.Item>
            {node.config?.triggerType === 'mqtt' && (
              <Form.Item label="MQTT 主题" help="支持 + / # 通配">
                <Input value={String(node.config?.topic || '')} onChange={(e) => updateConfig('topic', e.target.value)} placeholder="rtu/+/up/status" data-testid="rule-node-topic" />
              </Form.Item>
            )}
            {node.config?.triggerType === 'device' && (
              <Form.Item label="IMEI（可选）" help="留空=该租户所有设备">
                <Input value={String(node.config?.imei || '')} onChange={(e) => updateConfig('imei', e.target.value)} placeholder="864793080088193" data-testid="rule-node-imei" />
              </Form.Item>
            )}
            {node.config?.triggerType === 'schedule' && (
              <Form.Item label="Cron" help="6 段含秒，如 */5 * * * * *；平台每 30s 扫描">
                <Input value={String(node.config?.cron || '')} onChange={(e) => updateConfig('cron', e.target.value)} placeholder="*/5 * * * *" data-testid="rule-node-cron" />
              </Form.Item>
            )}
          </>
        )}

        {node.type === 'filter' && (
          <Form.Item label="过滤表达式" help="变量 msg；返回 true 继续，false 丢弃">
            <Input value={String(node.config?.expression || '')} onChange={(e) => updateConfig('expression', e.target.value)} placeholder="msg.data.v > 5" data-testid="rule-node-expression" />
          </Form.Item>
        )}

        {node.type === 'transform' && (
          <Form.Item label="Transform 脚本" help="定义 function transform(msg) { ... return msg; }；沙箱 50ms">
            <TextArea
              value={String(node.config?.script || '')}
              onChange={(e) => updateConfig('script', e.target.value)}
              rows={8}
              spellCheck={false}
              style={{ fontFamily: 'Consolas, monospace', fontSize: 12 }}
              placeholder={'function transform(msg) {\n  msg.tempF = msg.temp * 9 / 5 + 32;\n  return msg;\n}'}
              data-testid="rule-node-script"
            />
          </Form.Item>
        )}

        {node.type === 'enrich' && (
          <Form.Item label="富化来源">
            <Select
              value={String(node.config?.source || 'device')}
              onChange={(v) => updateConfig('source', v)}
              options={[
                { value: 'device', label: '设备信息（按 imei）' },
                { value: 'tenant', label: '租户信息' },
              ]}
              data-testid="rule-node-enrich-source"
            />
          </Form.Item>
        )}

        {node.type === 'action' && (
          <>
            <Form.Item label="动作类型">
              <Select
                value={String(node.config?.type || 'broadcast')}
                onChange={(v) => updateConfig('type', v)}
                options={[
                  { value: 'alert', label: '告警（设备日志）' },
                  { value: 'broadcast', label: '实时广播（WS）' },
                  { value: 'webhook', label: 'Webhook' },
                  { value: 'command', label: '设备命令' },
                ]}
                data-testid="rule-node-action-type"
              />
            </Form.Item>
            {node.config?.type === 'alert' && (
              <>
                <Form.Item label="级别">
                  <Select
                    value={String((node.config?.params as any)?.level || 'warn')}
                    onChange={(v) => updateParam('level', v)}
                    options={[{ value: 'info', label: 'info' }, { value: 'warn', label: 'warn' }, { value: 'error', label: 'error' }]}
                  />
                </Form.Item>
                <Form.Item label="消息">
                  <Input value={String((node.config?.params as any)?.message || '')} onChange={(e) => updateParam('message', e.target.value)} placeholder="规则触发" />
                </Form.Item>
              </>
            )}
            {node.config?.type === 'broadcast' && (
              <Form.Item label="事件名">
                <Input value={String((node.config?.params as any)?.event || '')} onChange={(e) => updateParam('event', e.target.value)} placeholder="rulechain:triggered" data-testid="rule-node-event" />
              </Form.Item>
            )}
            {node.config?.type === 'webhook' && (
              <>
                <Form.Item label="URL" help="仅 http/https">
                  <Input value={String((node.config?.params as any)?.url || '')} onChange={(e) => updateParam('url', e.target.value)} placeholder="https://..." data-testid="rule-node-url" />
                </Form.Item>
                <Form.Item label="方法">
                  <Select
                    value={String((node.config?.params as any)?.method || 'POST')}
                    onChange={(v) => updateParam('method', v)}
                    options={[{ value: 'POST', label: 'POST' }, { value: 'PUT', label: 'PUT' }]}
                  />
                </Form.Item>
              </>
            )}
            {node.config?.type === 'command' && (
              <Form.Item label="命令 action" help="RTU 命令名，如 get_status / reboot；带参命令用高级 JSON">
                <Input value={String((node.config?.params as any)?.action || '')} onChange={(e) => updateParam('action', e.target.value)} placeholder="get_status" data-testid="rule-node-command" />
              </Form.Item>
            )}
          </>
        )}

        {node.type === 'debug' && (
          <Form.Item label="标签">
            <Input value={String(node.config?.label || '')} onChange={(e) => updateConfig('label', e.target.value)} placeholder="调试输出" />
          </Form.Item>
        )}

        <Form.Item label="错误策略">
          <Select
            value={node.errorPolicy || 'stop'}
            onChange={(v) => updateNode({ errorPolicy: v as RuleChainNode['errorPolicy'] })}
            options={[
              { value: 'stop', label: 'stop（停止链）' },
              { value: 'skip', label: 'skip（跳过继续）' },
              { value: 'fallback', label: 'fallback（用输入继续）' },
            ]}
            data-testid="rule-node-error-policy"
          />
        </Form.Item>

        <Divider style={{ margin: '10px 0' }}>容错</Divider>
        <Form.Item label="重试次数" help="失败或超时后最多尝试次数（1~5，默认 1）">
          <InputNumber
            min={1}
            max={5}
            precision={0}
            value={node.retry?.maxAttempts ?? 1}
            onChange={(v) => updateNode({ retry: { ...(node.retry || {}), maxAttempts: Number(v) || 1 } })}
            style={{ width: '100%' }}
            data-testid="rule-node-retry-max"
          />
        </Form.Item>
        <Form.Item label="重试间隔(ms)" help="两次尝试之间等待，0~10000">
          <InputNumber
            min={0}
            max={10000}
            step={100}
            value={node.retry?.backoffMs ?? 0}
            onChange={(v) => updateNode({ retry: { ...(node.retry || {}), backoffMs: Number(v) || 0 } })}
            style={{ width: '100%' }}
            data-testid="rule-node-retry-backoff"
          />
        </Form.Item>
        <Form.Item label="节点超时(ms)" help="单次尝试超时，200~30000；默认 5000">
          <InputNumber
            min={200}
            max={30000}
            step={100}
            value={node.timeoutMs ?? 5000}
            onChange={(v) => updateNode({ timeoutMs: Number(v) || 5000 })}
            style={{ width: '100%' }}
            data-testid="rule-node-timeout"
          />
        </Form.Item>

        <Button danger icon={<DeleteOutlined />} onClick={removeNode} data-testid="rule-node-delete">删除节点</Button>
      </Form>
    </div>
  );
}
